import { useEffect, useState } from "react";
import { BellPlus, FilePlus2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, Empty, StatusBadge } from "../panel/PanelShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Field, SelectField, inputCls } from "../forms/Fields";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, fmtDateTime } from "../../lib/format";

const TYPES = { investment_plan: "Investment Planning", insurance_comparison: "Insurance Comparison", mf_research: "Mutual Fund Research", general: "General" };

function ReportDialog({ open, onClose, clients, presetUser, onSaved }) {
  const blank = { user_id: "", title: "", type: "investment_plan", summary: "", content: "" };
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF({ ...blank, user_id: presetUser || "" }); }, [open, presetUser]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = (c) => `${c.name} (${c.email})`;
  const save = async (e) => {
    e.preventDefault();
    if (!f.user_id) return toast.error("Select a client");
    try {
      await api.post("/admin/reports", f);
      toast.success("Report published to client portal");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Create client report</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4" data-testid="report-form">
          <SelectField label="Client" id="rp-client" value={clients.find((c) => c.user_id === f.user_id) ? label(clients.find((c) => c.user_id === f.user_id)) : ""} onChange={(v) => setF({ ...f, user_id: clients.find((c) => label(c) === v)?.user_id })} options={clients.map(label)} placeholder="Select client" testid="report-client-select" />
          <SelectField label="Report type" id="rp-type" value={TYPES[f.type]} onChange={(v) => setF({ ...f, type: Object.keys(TYPES).find((k) => TYPES[k] === v) })} options={Object.values(TYPES)} testid="report-type-select" />
          <Field label="Title" id="rp-title"><input id="rp-title" required minLength={3} className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="report-title-input" /></Field>
          <Field label="Summary" id="rp-sum"><input id="rp-sum" required minLength={3} className={inputCls} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="report-summary-input" /></Field>
          <Field label="Report content" id="rp-content"><Textarea id="rp-content" required minLength={10} rows={8} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="report-content-input" /></Field>
          <button data-testid="report-save-btn" className="rounded-full bg-navy py-3 text-sm font-semibold text-white">Publish report</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NotifyDialog({ client, onClose }) {
  const [f, setF] = useState({ title: "", message: "" });
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/clients/${client.user_id}/notify`, f);
      toast.success("Notification sent");
      setF({ title: "", message: "" });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Notify {client?.name}</DialogTitle></DialogHeader>
        <form onSubmit={send} className="grid gap-4" data-testid="notify-form">
          <Field label="Title" id="n-title"><input id="n-title" required minLength={2} className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="notify-title-input" /></Field>
          <Field label="Message" id="n-msg"><Textarea id="n-msg" required rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="notify-message-input" /></Field>
          <button data-testid="notify-send-btn" className="rounded-full bg-navy py-3 text-sm font-semibold text-white">Send to portal</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatsToggle({ client, onChanged }) {
  const auto = client.is_test && !client.exclude_from_stats;
  const set = async (counted) => {
    try {
      await api.patch(`/admin/clients/${client.user_id}/stats`, { exclude: !counted });
      toast.success(counted ? "Counted in public stats" : "Excluded from public stats");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <label className="flex items-center gap-2 text-xs text-slate-500" title={auto ? "Detected as a test account" : ""}>
      <Switch checked={!client.is_test} disabled={auto} onCheckedChange={set} data-testid={`client-stats-toggle-${client.user_id}`} />
      {auto ? "Test (auto)" : client.exclude_from_stats ? "Excluded" : "Counted"}
    </label>
  );
}

export function ClientsAdmin() {
  const [clients, setClients] = useState(null);
  const [notify, setNotify] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const load = () => api.get("/admin/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      {clients && !clients.length && <Empty text="No client accounts yet." testid="clients-empty" />}
      {!!clients?.length && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[760px] text-sm" data-testid="clients-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Name", "Email", "Phone", "City", "Login", "Goals", "Reports", "Joined", "Public stats", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.user_id} className="border-t border-slate-100" data-testid={`client-row-${c.user_id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{c.name}</td><td className="px-4 py-3">{c.email}</td><td className="px-4 py-3">{c.phone || "-"}</td><td className="px-4 py-3">{c.city || "-"}</td>
                  <td className="px-4 py-3 capitalize">{c.auth_provider}</td><td className="px-4 py-3 font-num">{c.goals}</td><td className="px-4 py-3 font-num">{c.reports}</td><td className="px-4 py-3 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3"><StatsToggle client={c} onChanged={load} /></td>
                  <td className="px-4 py-3"><div className="flex gap-1">
                    <button onClick={() => setNotify(c)} data-testid={`client-notify-${c.user_id}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Notify"><BellPlus size={15} /></button>
                    <button onClick={() => setReportFor(c.user_id)} data-testid={`client-report-${c.user_id}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Create report"><FilePlus2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <NotifyDialog client={notify} onClose={() => setNotify(null)} />
      <ReportDialog open={!!reportFor} presetUser={reportFor} clients={clients || []} onClose={() => setReportFor(null)} onSaved={load} />
    </div>
  );
}

export function ReportsAdmin() {
  const [rows, setRows] = useState(null);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/reports").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/admin/clients").then((r) => setClients(r.data)); }, []);
  const remove = async (id) => { if (window.confirm("Delete report?")) { await api.delete(`/admin/reports/${id}`); load(); } };
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="report-create-btn" className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy"><FilePlus2 size={16} /> New report</button></div>
      {rows && !rows.length && <Empty text="No reports published yet." testid="admin-reports-empty" />}
      <div className="grid gap-4 md:grid-cols-2">
        {(rows || []).map((r) => (
          <Card key={r.id} data-testid={`admin-report-${r.id}`}>
            <div className="flex justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wider text-mint-600">{TYPES[r.type]}</p><h3 className="mt-1 font-bold text-navy">{r.title}</h3><p className="text-sm text-slate-500">{r.client_name} · {fmtDate(r.created_at)}</p></div>
              <button onClick={() => remove(r.id)} data-testid={`admin-report-delete-${r.id}`} className="h-fit rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 size={15} /></button></div>
            <p className="mt-3 text-sm text-slate-600">{r.summary}</p>
          </Card>
        ))}
      </div>
      <ReportDialog open={open} clients={clients} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}

export function SupportAdmin() {
  const [rows, setRows] = useState(null);
  const [replying, setReplying] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get("/admin/support").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, body) => { await api.patch(`/admin/support/${id}`, body); toast.success("Ticket updated"); load(); };
  return (
    <div className="space-y-3">
      {rows && !rows.length && <Empty text="No support requests." testid="admin-support-empty" />}
      {(rows || []).map((t) => (
        <Card key={t.id} data-testid={`admin-ticket-${t.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><p className="font-semibold text-navy">{t.subject}</p><p className="text-xs text-slate-500">{t.user_name} · {t.user_email} · {t.category} · {fmtDateTime(t.created_at)}</p></div>
            <div className="flex items-center gap-2">
              <StatusBadge status={t.status} />
              {["open", "in_progress", "resolved"].filter((s) => s !== t.status).map((s) => <button key={s} onClick={() => update(t.id, { status: s })} data-testid={`ticket-${t.id}-set-${s}`} className="rounded-full border border-slate-200 px-3 py-1 text-xs capitalize text-slate-600 hover:bg-slate-50">{s.replace("_", " ")}</button>)}
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-600">{t.message}</p>
          {t.reply && <p className="mt-3 rounded-xl bg-mint/10 p-3 text-sm text-navy">Reply: {t.reply}</p>}
          {replying === t.id ? (
            <div className="mt-3 space-y-2"><Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`ticket-reply-input-${t.id}`} />
              <button onClick={async () => { await update(t.id, { reply, status: "in_progress" }); setReplying(null); }} data-testid={`ticket-reply-send-${t.id}`} className="rounded-full bg-navy px-5 py-2 text-sm font-semibold text-white">Send reply</button></div>
          ) : <button onClick={() => { setReplying(t.id); setReply(t.reply || ""); }} data-testid={`ticket-reply-btn-${t.id}`} className="mt-3 text-sm font-semibold text-mint-600">Reply</button>}
        </Card>
      ))}
    </div>
  );
}
